import { useTranslation } from 'react-i18next';
import SmallLetter from '../../../../../components/SmallLetter/SmallLetter';
import { IInitialState } from '../../../../../reduxService';
import { useAppSelector } from '../../../../../reduxService/hooks';
import { roundToThree, roundToTwo } from '../../../../MACRA_1/components/InputForm/Geometry/utils';
import { getActionByProjectType } from '../../../../actionMap';
import { conversionLabel } from '../../../../conversion';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { ISlopedGabionResults } from '../../../api/IApiResponse';
import { IProjectMacra2 } from '../../../store/project/projectSlice';
import { IWeirGeometrySlopedWeirForm } from '../../InputForm/WeirGeometry/IWeirGeometry';
import './HydraulicResults.scss';
import { IColumnType, Table } from '../../../../../components/Table/Table';
import { IData } from './HydraulicVertical';
interface IHydraulicSlopedProps {}

const HydraulicSloped = (props: IHydraulicSlopedProps) => {
    const { t } = useTranslation();
    const { selectResults, selectProject } = getActionByProjectType();
    const results: IInitialState<ISlopedGabionResults> = useAppSelector(selectResults as any);
    const project: IInitialState<IProjectMacra2> = useAppSelector(selectProject as any);
    const projectInfo = useAppSelector(selectProjectInfo);

    const symbolValueUnitColumns: IColumnType<IData>[] = [
        {
            key: 'symbol',
            title: t('output_symbol'),
            width: '15%',
        },
        {
            key: 'value',
            title: t('output_value'),
            width: '20%',
            render: (_, row) => {
                if (typeof row.value === 'boolean') {
                    if (row.value) return <i className='fa-solid fa-check d-flex justify-content-center align-items-center' style={{ color: '#004652' }} />;
                    else return <i className='fa-solid fa-x d-flex justify-content-center align-items-center' style={{ color: '#E31937' }} />;
                } else {
                    return row.value;
                }
            },
        },
        {
            key: 'unit',
            title: t('output_unit'),
            width: '15%',
        },
    ];
    const columnsInput: IColumnType<IData>[] = [
        {
            key: 'label',
            title: t('output_table_title_input'),
        },
        ...symbolValueUnitColumns,
    ];
    const columnsUpstream: IColumnType<IData>[] = [
        {
            key: 'label',
            title: t('output_hydraulic_sloped_upstream_parameters'),
        },
        ...symbolValueUnitColumns,
    ];
    const columnsDownstream: IColumnType<IData>[] = [
        {
            key: 'label',
            title: t('output_hydraulic_sloped_downstream_parameters'),
        },
        ...symbolValueUnitColumns,
    ];
    const input: IData[] = [
        {
            label: t('output_hydraulic_stepped_sloped_discharge'),
            symbol: 'Q',
            value: roundToTwo(results.data?.hydraulic.designDischarge),
            unit: conversionLabel.m3s_ft3s[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_sloped_riverbed_gradient'),
            symbol: 'i',
            value: roundToTwo(project.data?.hydraulicData?.riverBedGradient),
            unit: '-',
        },
        {
            label: t('output_hydraulic_stepped_sloped_roughnessCoefficient'),
            symbol: 'n',
            value: roundToThree(project.data?.hydraulicData?.roughnessCoefficient),
            unit: '-',
        },
        {
            label: t('output_hydraulic_sloped_chute_width'),
            symbol: 'W',
            value: roundToTwo(results.data?.hydraulic.wingsWidth),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_sloped_chute_slope'),
            symbol: <SmallLetter normal='S' small='0' />,
            value: roundToTwo(results.data?.hydraulic.chuteSlope),
            unit: '-',
        },
        {
            label: t('output_hydraulic_sloped_chute_height'),
            symbol: <SmallLetter normal='H' small='c' />,
            value: roundToTwo((project.data?.weirGeometry as IWeirGeometrySlopedWeirForm).height),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_sloped_crest_length'),
            symbol: <SmallLetter normal='L' small='c' />,
            value: roundToTwo(results.data?.hydraulic.crestLenght),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
    ];
    const upstream: IData[] = [
        {
            label: t('output_hydraulic_sloped_unit_discharge'),
            symbol: 'q',
            value: roundToTwo(results.data?.hydraulic.specificDischargeAtCrest),
            unit: conversionLabel.m3sm_ft3sft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_sloped_backwater_depth_upstream'),
            symbol: <SmallLetter normal='z' small='0' />,
            value: roundToTwo(results.data?.hydraulic.upstreamSection),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_sloped_critical_height_crest'),
            symbol: <SmallLetter normal='h' small='g' />,
            value: roundToTwo(results.data?.hydraulic.criticalHeight),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_sloped_critical_velocity_crest'),
            symbol: <SmallLetter normal='v' small='g' />,
            value: roundToTwo(results.data?.hydraulic.criticalVelocity),
            unit: conversionLabel.ms_fts[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_sloped_critical_head_crest'),
            symbol: <SmallLetter normal='H' small='g' />,
            value: roundToTwo(results.data?.hydraulic.criticalHeadAtCrest),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
    ];
    const downstream: IData[] = [
        {
            label: t('output_hydraulic_sloped_velocity_downstream'),
            symbol: <SmallLetter normal='v' small='1' />,
            value: roundToTwo(results.data?.basinDesign.downstreamVelocity),
            unit: conversionLabel.ms_fts[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_sloped_water_depth_downstream'),
            symbol: <SmallLetter normal='z' small='1' />,
            value: roundToTwo(results.data?.basinDesign.downstreamWaterDepth),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_sloped_froude_number'),
            symbol: 'Fr',
            value: roundToTwo(results.data?.basinDesign.froudeNumber),
            unit: '-',
        },
        {
            label: t('output_hydraulic_sloped_conjugated_height'),
            symbol: <SmallLetter normal='z' small='2' />,
            value: roundToTwo(results.data?.basinDesign.conjugateDepth),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_sloped_hydraulic_jump_length'),
            symbol: <SmallLetter normal='L' small='j' />,
            value: roundToTwo(results.data?.basinDesign.jumpLength),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_sloped_water_depth_downstream_structure'),
            symbol: <SmallLetter normal='z' small='3' />,
            value: roundToTwo(results.data?.hydraulic.z3),
            unit: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number],
        },
        {
            label: t('output_hydraulic_sloped_velocity_downstream_structure'),
            symbol: <SmallLetter normal='v' small='3' />,
            value: roundToTwo(results.data?.hydraulic.v3),
            unit: conversionLabel.ms_fts[projectInfo.data?.unitOfMeasure as number],
        },
    ];

    return (
        <div className='d-flex justify-content-center align-items-center gap-3 w-100 overflow-auto'>
            <div className='d-flex justify-content-evenly align-items-center gap-3 w-50 static-results'>
                <div className='d-flex justify-content-evenly align-items-center gap-3 w-100 static-results'>
                    <Table columns={columnsInput} data={input} className='w-100' />
                </div>
            </div>
            <div className='d-flex flex-column justify-content-evenly align-items-center gap-3 w-50 static-results'>
                <label>{t('output_hydraulic_sloped_output')}</label>
                <Table columns={columnsUpstream} data={upstream} className='w-100' />
                <Table columns={columnsDownstream} data={downstream} className='w-100' />
            </div>
        </div>
    );
};

export default HydraulicSloped;
